import { useEffect, useRef, useState, useCallback } from "react";
import { Play } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { youtubeEmbed } from "@/data/site";

const MAX_IFRAMES = typeof window !== "undefined" && window.innerWidth < 768 ? 6 : 12;
const active = new Set();
const waiters = new Set();
const acquire = (id) => {
  if (active.has(id)) return true;
  if (active.size >= MAX_IFRAMES) return false;
  active.add(id);
  return true;
};
const release = (id) => {
  if (active.delete(id)) waiters.forEach((f) => f());
};

const isSlowConnection = () => {
  const c = navigator.connection || {};
  return !!c.saveData || ["2g", "3g"].includes(c.effectiveType);
};

export const VideoFrame = ({ video, className = "" }) => {
  const ref = useRef(null);
  const nearRef = useRef(false);
  const loadedRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [slow] = useState(isSlowConnection);
  const { open } = usePlayer();
  const id = video.id;

  const tryLoad = useCallback(() => {
    if (loadedRef.current || slow || !nearRef.current) return;
    if (acquire(id)) {
      loadedRef.current = true;
      setLoaded(true);
    }
  }, [id, slow]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const near = new IntersectionObserver(
      ([e]) => {
        nearRef.current = e.isIntersecting;
        if (e.isIntersecting) tryLoad();
      },
      { rootMargin: "500px" }
    );
    const far = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting && loadedRef.current) {
          loadedRef.current = false;
          setLoaded(false);
          release(id);
        }
      },
      { rootMargin: "900px" }
    );
    near.observe(el);
    far.observe(el);
    waiters.add(tryLoad);
    return () => {
      near.disconnect();
      far.disconnect();
      waiters.delete(tryLoad);
      if (loadedRef.current) {
        loadedRef.current = false;
        release(id);
      }
    };
  }, [id, tryLoad]);

  const tall = video.aspect === "9:16";

  return (
    <div
      ref={ref}
      data-testid={`video-tile-${id}`}
      onClick={() => open(video)}
      className={`relative overflow-hidden rounded-xl md:rounded-2xl bg-black cursor-pointer group ${
        tall ? "row-span-2" : "col-span-2"
      } ${className}`}
      role="button"
      aria-label={`Play ${video.industry} video`}
    >
      <img
        src={`/thumbs/${video.guid}.jpg`}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {loaded ? (
        <iframe
          src={
            video.youtubeId
              ? youtubeEmbed(video.youtubeId, { muted: true })
              : `${video.embedUrl}?autoplay=true&loop=true&muted=true&preload=false&responsive=true`
          }
          loading="lazy"
          title={`${video.industry} — ${video.capability}`}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0 pointer-events-none"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          {slow ? (
            <span className="w-12 h-12 rounded-full bg-[#C8F031] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-lg">
              <Play className="w-5 h-5 text-black fill-black ml-0.5" />
            </span>
          ) : (
            <span className="tile-pulse w-10 h-10 rounded-full bg-black/45 backdrop-blur-sm border border-white/20 flex items-center justify-center">
              <Play className="w-3.5 h-3.5 text-white fill-white ml-0.5" />
            </span>
          )}
        </div>
      )}
      <span className="absolute inset-0 z-10 rounded-xl md:rounded-2xl ring-1 ring-inset ring-white/5 group-hover:ring-[#C8F031]/40 transition duration-300 pointer-events-none" />
    </div>
  );
};

export default VideoFrame;
